/**
 * "Merlin found answers" (kanban 3eed0be5): the words for a found-answer
 * row and the split between what waits on the reader and what already
 * closed. Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import type { FoundAnswerEvidence, FoundAnswerRead } from '../../services/qcpFoundAnswers';

/** What kind of item it is, in Jess's words. */
export function kindWord(row: Pick<FoundAnswerRead, 'item_kind'>): string {
  switch (row.item_kind) {
    case 'ask':
      return 'ask';
    case 'case_task':
      return 'checklist step';
    case 'thread_wait':
      return 'wait';
    default:
      return 'item';
  }
}

/** The question's first line (an ask's title; a step's label). */
export function questionTitle(row: Pick<FoundAnswerRead, 'question'>): string {
  return (row.question ?? '').split('\n')[0].trim();
}

/** "QT-10070" / "INV-00003" / "" — where the item lives. */
export function placeWord(row: Pick<FoundAnswerRead, 'thread_no' | 'investigation_no'>): string {
  return row.investigation_no || row.thread_no || '';
}

/** A source id as a word: "meeting", "mail", "order AU02400", "chat"… */
export function sourceWord(evidenceId: string): string {
  const [source, ...rest] = evidenceId.split(':');
  const ref = rest.join(':');
  switch (source) {
    case 'meeting':
      return 'a meeting';
    case 'notes':
      return "Merlin's meeting notes";
    case 'synced_mail':
    case 'sent_mail':
      return 'an email';
    case 'order':
      return `order ${ref}`;
    case 'invoice':
      return 'an invoice';
    case 'complaint':
      return 'the complaint record';
    case 'thread_event':
    case 'case_event':
      return 'the item itself';
    case 'chat':
      return 'your chat with Merlin';
    case 'ask_answer':
      return "someone else's answer";
    default:
      return source || 'a source';
  }
}

/** One line per evidence row: source, date, the quote when there is one. */
export function evidenceLine(e: FoundAnswerEvidence): string {
  const when = e.when ? ` (${e.when})` : '';
  const quote = e.quote ? ` — “${e.quote}”` : '';
  return `${sourceWord(e.id)}${when}${quote}`;
}

/** "9 in 10" from a 0–1 confidence; "" when unknown. */
export function confidenceWord(confidence: number | null): string {
  if (confidence == null || Number.isNaN(confidence)) return '';
  const tenths = Math.round(Math.max(0, Math.min(1, confidence)) * 10);
  return `${tenths} in 10`;
}

/** Who closed it: "Merlin" or "you". */
export function retiredByWord(row: Pick<FoundAnswerRead, 'retired_by'>): string {
  return (row.retired_by ?? '').startsWith('merlin:') ? 'Merlin' : 'you';
}

export interface FoundAnswersSplit {
  /** Waiting on the reader: Confirm or Still open. Oldest first as served. */
  candidates: FoundAnswerRead[];
  /** Already closed with the answer (by Merlin or by the reader), newest first. */
  closed: FoundAnswerRead[];
}

export function splitFoundAnswers(rows: FoundAnswerRead[]): FoundAnswersSplit {
  const candidates = rows.filter((r) => r.status === 'candidate');
  const closed = rows
    .filter((r) => r.status === 'retired')
    .sort((a, b) => (b.retired_at ?? '').localeCompare(a.retired_at ?? ''));
  return { candidates, closed };
}

/* ---------------------------------------------------------------- */
/* Was the ask ever sent? (UX round 2026-11-03)                       */
/* ---------------------------------------------------------------- */

/** Where an ask stands with the person it names: a draft nobody received,
 *  or sent (when, and how many replies came back). */
export type AskSendState =
  | { kind: 'draft' }
  | { kind: 'sent'; at: string | null; replies: number };

/**
 * The ask behind a found-answer row, from the door's own asks list. Null
 * for a checklist step or a wait, or an ask the list does not hold.
 *
 * Every card said "looks answered" whether the question had gone out or
 * not: the explorer confirmed "answers" to four drafts nobody ever
 * received, and learned they were drafts only by asking Merlin.
 */
export function askSendState(
  row: Pick<FoundAnswerRead, 'item_kind' | 'item_id'>,
  asks: ReadonlyArray<DoorDataRequestRead>,
): AskSendState | null {
  if (row.item_kind !== 'ask') return null;
  const ask = asks.find((a) => a.id === row.item_id);
  if (!ask) return null;
  if (ask.status === 'proposed' && !ask.sent_at) return { kind: 'draft' };
  // The sweep's own find is not a reply from the person.
  const replies = (ask.responses ?? []).filter((r) => r.channel !== 'found').length;
  return { kind: 'sent', at: ask.sent_at ?? null, replies };
}

function shortDate(iso: string | null): string {
  const t = iso ? new Date(iso) : null;
  return t && !Number.isNaN(t.getTime()) ? t.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
}

/** "a draft, never sent" / "sent Oct 7, no reply yet" / "sent Oct 7, 1 reply". */
export function sendStateWords(state: AskSendState): string {
  if (state.kind === 'draft') return 'a draft, never sent';
  const when = shortDate(state.at);
  const sent = when ? `sent ${when}` : 'sent';
  if (state.replies === 0) return `${sent}, no reply yet`;
  return `${sent}, ${state.replies} ${state.replies === 1 ? 'reply' : 'replies'}`;
}

/**
 * The verdict, said honestly. An unsure ruling "may be answered" (it never
 * "looks answered"); a question nobody replied to was answered elsewhere,
 * if at all, so the words say where the answer came from.
 */
export function verdictWords(
  row: Pick<FoundAnswerRead, 'verdict' | 'confidence'>,
  state: AskSendState | null,
): string {
  const noReply = !!state && (state.kind === 'draft' || state.replies === 0);
  const sure = confidenceWord(row.confidence);
  if (row.verdict === 'unsure') {
    return noReply ? 'may be answered elsewhere (Merlin is not sure)' : 'may be answered (Merlin is not sure)';
  }
  const how = noReply ? 'looks answered elsewhere' : 'looks answered';
  return sure ? `${how} (${sure} sure)` : how;
}

/** What a press did, kept where the card was (UX round 2026-11-03: the
 *  card vanished, the list moved under the pointer, and the next press
 *  closed a different question with nothing saying which). */
export type FoundAnswerOutcome = 'confirmed' | 'kept_open';

export function outcomeWords(outcome: FoundAnswerOutcome): string {
  return outcome === 'confirmed'
    ? 'Closed with Merlin\u2019s answer'
    : 'Left open: Merlin stops showing it here and checks it again later';
}
